import type Highcharts from 'highcharts';
import { isLegendAxisDivider } from './doubleYAxisLegend';
import { hex2rgba } from './hex2rgba';
import { formatTooltipHtml } from './tooltipHtml';

const LEGEND_TOOLTIP_BOUND_ATTR = 'data-line-chart-legend-tooltip-bound';

type LegendChart = Highcharts.Chart & { legendTooltip?: Highcharts.SVGElement };

type LegendDomElement = HTMLElement | SVGElement;

type LegendItemRecord = { name?: string; legendItem?: unknown; series?: Highcharts.Series };

export const getLegendDisplayText = (itemEl: LegendDomElement): string => {
  if (itemEl instanceof HTMLElement) {
    const spans = itemEl.querySelectorAll('span');
    const labelSpan = spans.length > 0 ? spans[spans.length - 1] : null;
    if (labelSpan?.textContent) {
      return labelSpan.textContent.replace(/\s+/g, ' ').trim();
    }
  }

  const textNode = itemEl.querySelector('text, tspan');
  if (textNode?.textContent) {
    return textNode.textContent.replace(/\s+/g, ' ').trim();
  }

  return (itemEl.textContent ?? '').replace(/\s+/g, ' ').trim();
};

export const isLegendLabelTruncated = (itemEl: LegendDomElement, fullName: string): boolean => {
  const normalizedName = fullName.trim();
  if (!normalizedName) {
    return false;
  }

  const displayText = getLegendDisplayText(itemEl);
  if (!displayText || displayText === normalizedName) {
    return false;
  }

  if (itemEl instanceof HTMLElement) {
    const labelNode =
      (itemEl.querySelector('span:last-of-type') as HTMLElement | null) ??
      (itemEl.querySelector('span') as HTMLElement | null);
    if (labelNode && labelNode.scrollWidth > labelNode.clientWidth + 1) {
      return true;
    }
    if (labelNode && labelNode.scrollHeight > labelNode.clientHeight + 1) {
      return true;
    }
  }

  if (/(\.{3}|…)\s*$/.test(displayText)) {
    return true;
  }

  return displayText.length < normalizedName.length;
};

const normalizeLegendDescription = (description?: string | null): string | undefined => {
  const trimmed = typeof description === 'string' ? description.trim() : '';
  return trimmed || undefined;
};

export const hasLineChartLegendDescription = (description?: string | null): boolean =>
  Boolean(normalizeLegendDescription(description));

const isLegendDisplayTextTruncated = (fullName: string, displayText?: string | null): boolean => {
  const normalizedName = fullName.trim();
  const normalizedDisplay = typeof displayText === 'string' ? displayText.replace(/\s+/g, ' ').trim() : '';
  if (!normalizedName || !normalizedDisplay || normalizedDisplay === normalizedName) {
    return false;
  }
  if (/(\.{3}|…)\s*$/.test(normalizedDisplay)) {
    return true;
  }
  return normalizedDisplay.length < normalizedName.length;
};

/** Show tooltip when the legend label is truncated or the series has a BE description. */
export const shouldShowLineChartLegendTooltip = (
  fullName: string,
  displayText?: string | null,
  description?: string | null,
): boolean => {
  if (hasLineChartLegendDescription(description)) {
    return Boolean(fullName.trim());
  }
  return isLegendDisplayTextTruncated(fullName, displayText);
};

export const shouldShowLineChartLegendItemTooltip = (
  itemEl: LegendDomElement,
  fullName: string,
  description?: string | null,
): boolean => {
  if (hasLineChartLegendDescription(description)) {
    return Boolean(fullName.trim());
  }
  return isLegendLabelTruncated(itemEl, fullName);
};

export const hideLineChartLegendTooltip = (chart: LegendChart | undefined) => {
  if (chart?.legendTooltip) {
    chart.legendTooltip.destroy();
    chart.legendTooltip = undefined;
  }
};

const resolveSeriesDescription = (series: Highcharts.Series, chart?: Highcharts.Chart): string | undefined => {
  const options = series.options as { custom?: { description?: string }; description?: string; name?: string };
  const description = options?.custom?.description ?? options?.description;
  if (typeof description === 'string' && description.trim()) {
    return description.trim();
  }

  if (chart && series.name) {
    const userSeries =
      (chart.userOptions?.series as Array<{ name?: string; custom?: { description?: string } }> | undefined) ?? [];
    const match = userSeries.find(candidate => candidate.name === series.name);
    const userDescription = match?.custom?.description;
    if (typeof userDescription === 'string' && userDescription.trim()) {
      return userDescription.trim();
    }
  }

  return undefined;
};

const coerceLegendDom = (value: unknown): LegendDomElement | null => {
  if (value instanceof HTMLElement || value instanceof SVGElement) {
    return value;
  }
  if (value && typeof value === 'object') {
    const record = value as { element?: unknown; group?: { element?: unknown } };
    if (record.element instanceof HTMLElement || record.element instanceof SVGElement) {
      return record.element;
    }
    if (record.group?.element instanceof HTMLElement || record.group?.element instanceof SVGElement) {
      return record.group.element;
    }
  }
  return null;
};

const getLegendDomItems = (chart: Highcharts.Chart): HTMLElement[] =>
  Array.from(
    chart.container?.querySelectorAll('.highcharts-legend-item:not(.double-y-axis-legend-divider-item)') ?? [],
  ) as HTMLElement[];

const getLegendItemNames = (chart: Highcharts.Chart): string[] => {
  const allItems = (chart.legend as { allItems?: LegendItemRecord[] } | undefined)?.allItems ?? [];
  return allItems.map(item => item.name).filter((name): name is string => Boolean(name) && !isLegendAxisDivider(name));
};

const resolveSeriesNameByPrefix = (chart: Highcharts.Chart, displayText: string): string | null => {
  const normalizedDisplay = displayText.replace(/\s+/g, ' ').trim();
  if (!normalizedDisplay) {
    return null;
  }

  const withoutEllipsis = normalizedDisplay.replace(/(\.{3}|…)\s*$/, '').trim();
  const candidates = chart.series.filter(series => {
    if (!series.name || /^Navigator \d+$/.test(series.name)) {
      return false;
    }
    const options = series.options as { showInLegend?: boolean; custom?: { isMeanAggregate?: boolean } };
    if (options.showInLegend === false || options.custom?.isMeanAggregate) {
      return false;
    }
    return true;
  });

  const exactMatch = candidates.find(series => series.name === normalizedDisplay);
  if (exactMatch?.name) {
    return exactMatch.name;
  }

  if (withoutEllipsis) {
    const prefixMatches = candidates.filter(series => series.name?.startsWith(withoutEllipsis));
    if (prefixMatches.length === 1 && prefixMatches[0].name) {
      return prefixMatches[0].name;
    }
  }

  return null;
};

const resolveSeriesDescriptionFromChart = (chart: Highcharts.Chart, seriesName: string): string | undefined => {
  const optionsCustom = (chart.options as { custom?: { seriesDescriptionByName?: Record<string, string> } } | undefined)
    ?.custom;
  const chartCustom = (
    chart.options.chart as { custom?: { seriesDescriptionByName?: Record<string, string> } } | undefined
  )?.custom;
  const mappedDescription =
    optionsCustom?.seriesDescriptionByName?.[seriesName] ?? chartCustom?.seriesDescriptionByName?.[seriesName];
  if (typeof mappedDescription === 'string' && mappedDescription.trim()) {
    return mappedDescription.trim();
  }

  const userCustom = (
    chart.userOptions as { custom?: { seriesDescriptionByName?: Record<string, string> } } | undefined
  )?.custom?.seriesDescriptionByName?.[seriesName];
  if (typeof userCustom === 'string' && userCustom.trim()) {
    return userCustom.trim();
  }

  const series = chart.series.find(candidate => candidate.name === seriesName);
  if (!series) {
    return undefined;
  }

  return resolveSeriesDescription(series, chart);
};

const resolveSeriesNameFromLegendItem = (chart: Highcharts.Chart, itemEl: LegendDomElement): string | null => {
  const allItems = (chart.legend as { allItems?: LegendItemRecord[] } | undefined)?.allItems ?? [];

  for (const item of allItems) {
    if (!item.name || isLegendAxisDivider(item.name)) {
      continue;
    }

    const dom = coerceLegendDom(item.legendItem);
    const legendRow =
      dom instanceof Element ? (dom.closest('.highcharts-legend-item') as LegendDomElement | null) : null;
    const matchEl = legendRow ?? dom;
    if (matchEl && (matchEl === itemEl || itemEl.contains(matchEl) || matchEl.contains(itemEl))) {
      return item.name;
    }
  }

  if (itemEl instanceof Element) {
    const domItems = getLegendDomItems(chart);
    const names = getLegendItemNames(chart);
    const index = domItems.indexOf(itemEl as HTMLElement);
    if (index >= 0 && index < names.length) {
      return names[index];
    }
  }

  const displayText = getLegendDisplayText(itemEl);
  return resolveSeriesNameByPrefix(chart, displayText);
};

const showLineChartLegendTooltip = (
  chart: LegendChart,
  itemRect: DOMRect | null | undefined,
  chartContainerRect: DOMRect | null | undefined,
  name: string,
  description: string | undefined,
  tooltipCfg: any,
) => {
  hideLineChartLegendTooltip(chart);

  const tooltipHtml = formatTooltipHtml({
    title: name,
    description,
    tooltipCfg,
    letterSpacingAdjustment: -0.75,
  });

  chart.legendTooltip = chart.renderer
    .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
    .css({
      color: tooltipCfg.color,
      width: tooltipCfg.maxWidth,
      opacity: 0,
      zIndex: 11111,
    })
    .attr({
      className: 'tooltip',
      class: 'tooltip legendLabel',
      fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
      stroke: tooltipCfg.borderColor,
      zIndex: 11111,
      'dominant-baseline': 'auto',
      'stroke-width': tooltipCfg.borderWidth,
      'stroke-dasharray':
        tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
      'stroke-opacity': tooltipCfg.opacity,
      padding: Number(tooltipCfg.padding.replace('px', '')),
      r: Number(tooltipCfg.borderRadius.replace('px', '')),
    })
    .add()
    .toFront();

  const tooltipRect = chart.legendTooltip.getBBox();
  const tooltipBorderWidth = Number(String(tooltipCfg.borderWidth).replace('px', '')) || 0;
  let tooltipX =
    itemRect && chartContainerRect
      ? itemRect.left - chartContainerRect.left - tooltipRect.width - 8
      : (chart.plotLeft ?? 0);
  let tooltipY =
    itemRect && chartContainerRect
      ? itemRect.top - chartContainerRect.top + itemRect.height / 2 - tooltipRect.height / 2
      : (chart.plotTop ?? 0);

  if (tooltipX < tooltipBorderWidth) {
    tooltipX = tooltipBorderWidth;
  }
  if (chartContainerRect && tooltipX + tooltipRect.width > chartContainerRect.width) {
    tooltipX = chartContainerRect.width - tooltipRect.width - tooltipBorderWidth;
  }
  if (tooltipY < tooltipBorderWidth) {
    tooltipY = tooltipBorderWidth;
  }

  chart.legendTooltip
    .attr({
      x: tooltipX,
      y: tooltipY,
      zIndex: 11111,
    })
    .css({
      opacity: 1,
      zIndex: 11111,
    });
};

const handleLegendTooltipPointer = (chart: Highcharts.Chart, event: MouseEvent, tooltipCfg: any) => {
  const itemEl = (event.target as Element | null)?.closest?.(
    '.highcharts-legend-item:not(.double-y-axis-legend-divider-item)',
  ) as LegendDomElement | null;
  if (!itemEl || !chart.container?.contains(itemEl)) {
    return;
  }

  const seriesName = resolveSeriesNameFromLegendItem(chart, itemEl);
  if (!seriesName || isLegendAxisDivider(seriesName)) {
    return;
  }

  const description = resolveSeriesDescriptionFromChart(chart, seriesName);
  if (!shouldShowLineChartLegendItemTooltip(itemEl, seriesName, description)) {
    return;
  }

  const chartContainer = chart.container?.getBoundingClientRect?.();
  const itemRect = itemEl.getBoundingClientRect?.();
  showLineChartLegendTooltip(chart as LegendChart, itemRect, chartContainer, seriesName, description, tooltipCfg);
};

const bindLegendTooltipDelegation = (chart: Highcharts.Chart, tooltipCfg: any) => {
  const container = chart.container;
  if (!container || container.getAttribute(LEGEND_TOOLTIP_BOUND_ATTR) === '1') {
    return;
  }
  container.setAttribute(LEGEND_TOOLTIP_BOUND_ATTR, '1');

  container.addEventListener(
    'mouseover',
    (event: MouseEvent) => {
      handleLegendTooltipPointer(chart, event, tooltipCfg);
    },
    true,
  );

  container.addEventListener(
    'mouseout',
    (event: MouseEvent) => {
      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Element && nextTarget.closest('.highcharts-legend-item')) {
        return;
      }
      hideLineChartLegendTooltip(chart as LegendChart);
    },
    true,
  );
};

export const syncLineChartLegendTooltips = (chart: Highcharts.Chart | undefined, tooltipCfg: any) => {
  if (!tooltipCfg) {
    hideLineChartLegendTooltip(chart as LegendChart);
    return;
  }

  const legend = chart?.legend as (Highcharts.Legend & { display?: boolean }) | undefined;
  if (!chart || !legend || legend.display === false || legend.options?.enabled === false) {
    hideLineChartLegendTooltip(chart as LegendChart);
    return;
  }

  bindLegendTooltipDelegation(chart, tooltipCfg);
};
